import { DIRECT_MESSAGE_MAX_LENGTH } from 'model';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './MessageItem.module.css';
import classNames from 'classnames';
import { IconAlert, IconCheck, IconClose, IconPaperAirplane, IconUpload } from './QuirkIcons/index.js';
import { useIonActionSheet } from '@ionic/react';
import { IconButton } from './IconButton.js';
import { FlexibleTextarea } from './Forms/FlexibleTextarea.js';
import { MessageTime } from './MessageTime.js';
import { DirectMessageState, deleteMessage, updateMessage } from '../state/messagesSlice.js';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';

type MessageItemProps = {
	item: DirectMessageState;
	readOnly?: boolean;
};

function useSentMessageActionSheet(): (onDidDismiss: (event: CustomEvent) => void) => void {
	const [presentSheet] = useIonActionSheet();
	return useCallback(
		(onDidDismiss: (event: CustomEvent) => void) => {
			const buttons = [
				{
					text: 'Edit message',
					data: {
						action: 'edit'
					}
				},
				{
					text: 'Delete message',
					data: {
						action: 'delete'
					}
				},
				{
					text: 'Cancel',
					role: 'cancel',
					data: {
						action: 'cancel'
					}
				}
			];
			presentSheet({
				header: 'Message Options',
				buttons,
				onDidDismiss
			});
		},
		[presentSheet]
	);
}

export const SentMessageItem: React.FC<MessageItemProps> = ({ item, readOnly }) => {
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
	const [state, setState] = useState({ message: item.message, isEditing: false });
	const showSheet = useSentMessageActionSheet();
	const onDismissSheet = (event: CustomEvent) => {
		if (event.detail?.data?.action === 'edit') {
			setState(current => ({ ...current, isEditing: true }));
		} else if (event.detail?.data?.action === 'delete') {
			setState(current => ({ ...current, isEditing: false }));
			dispatch(deleteMessage(item._id));
		}
	};

	const handleItemClick = () => {
		if (!state.isEditing && !readOnly) {
			showSheet(onDismissSheet);
		}
	};

	const handleMessageChange = (value: string) => {
		setState(current => ({
			...current,
			message: value
		}));
	};

	const handleUpdateMessage = async () => {
		if (state.message === '') {
			return;
		}
		setState(current => ({ ...current, isEditing: false }));
		dispatch(
			updateMessage({
				...item,
				message: state.message
			})
		);
	};

	const textareaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (state.isEditing) {
			textareaRef.current?.focus();
		}
	}, [state.isEditing]);

	useEffect(() => {
		setState(current => {
			if (current.isEditing) {
				return current;
			}
			return {
				...current,
				message: item.message
			};
		});
	}, [item.message]);

	return (
		<div
			className={classNames(styles.MessageItem, styles.sent)}
			onClick={handleItemClick}
			data-testid={`SentMessageItem-${item._id}`}
		>
			<div className={styles.messageContainer}>
				{!state.isEditing && (
					<div className={styles.message} data-testid={'SentMessageItem-message'}>
						{item.message}
					</div>
				)}
				{state.isEditing && (
					<div className={styles.editContainer}>
						<IconButton
							icon={<IconClose />}
							onClick={() => setState(current => ({ ...current, isEditing: false }))}
						/>
						<div>
							<FlexibleTextarea
								value={state.message}
								onChange={handleMessageChange}
								onEnter={handleUpdateMessage}
								ref={textareaRef}
								maxLength={DIRECT_MESSAGE_MAX_LENGTH}
							/>
						</div>
						<IconButton
							icon={<IconPaperAirplane />}
							disabled={!state.message}
							onClick={handleUpdateMessage}
						/>
					</div>
				)}
				<div className={classNames([styles.created, styles.read])}>
					<MessageTime time={item.created} />
					{item.read && (
						<>
							&nbsp;&middot;&nbsp;
							<IconCheck size="tiny" color="var(--ion-color-primary-tint)" />
						</>
					)}
					{item.requestId && (
						<>
							&nbsp;&middot;&nbsp;
							<IconUpload size="tiny" color="var(--ion-color-success)" />
						</>
					)}

					{item.error && (
						<>
							&nbsp;&middot;&nbsp;
							<IconAlert size="tiny" color="var(--ion-color-danger)" />
						</>
					)}
				</div>
			</div>
		</div>
	);
};
